import clsx from "clsx"
import { FC, memo, useId, useLayoutEffect, useRef, useState } from "react"
import type { IArcText } from "."
import styles from "./ArcText.module.scss"

const getPathD = (width: number, height: number, arcHeight: number) => {
  const centerY = height / 2
  const curveHeight = (width * arcHeight) / 100
  const controlY = centerY - curveHeight
  return `M 0,${centerY} Q ${width / 2},${controlY} ${width},${centerY}`
}

const ArcTextComponent: FC<IArcText> = ({
  text,
  arcHeight = 6,
  minFontSize = 1,
  maxFontSize,
  className,
  ...props
}) => {
  const pathId = useId()
  const containerRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<SVGTextElement>(null)
  const pathRef = useRef<SVGPathElement>(null)

  const [dimensions, setDimensions] = useState({ width: 100, height: 30 })
  const [computedMaxFontSize, setComputedMaxFontSize] = useState(
    maxFontSize ?? 100
  )
  const [fontSize, setFontSize] = useState(computedMaxFontSize)
  const [isReady, setIsReady] = useState(false)

  const resolvedMaxFontSize = maxFontSize ?? computedMaxFontSize

  const measureAndFit = () => {
    const measureEl = measureRef.current
    const pathEl = pathRef.current
    if (!measureEl || !pathEl) return

    const textLength = measureEl.getComputedTextLength()
    if (textLength === 0) return

    const pathLength = pathEl.getTotalLength()
    const ratio = pathLength / textLength
    const calculated = resolvedMaxFontSize * ratio
    const final = Math.max(
      minFontSize,
      Math.min(resolvedMaxFontSize, Math.floor(calculated))
    )

    setFontSize(final)
    setIsReady(true)
  }

  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) return

    const update = () => {
      const width = container.offsetWidth
      const height = container.offsetHeight
      if (width > 0 && height > 0) {
        setDimensions((prev) =>
          prev.width === width && prev.height === height
            ? prev
            : { width, height }
        )
      }
      if (maxFontSize === undefined) {
        const cssFontSize = parseFloat(
          window.getComputedStyle(container).fontSize
        )
        if (cssFontSize > 0) setComputedMaxFontSize(cssFontSize)
      }
    }

    const observer = new ResizeObserver(update)
    observer.observe(container)
    update()

    return () => observer.disconnect()
  }, [maxFontSize])

  useLayoutEffect(() => {
    if (dimensions.width === 0) return

    document.fonts.ready.then(measureAndFit).catch(measureAndFit)
  }, [text, dimensions, minFontSize, resolvedMaxFontSize])

  const pathD = getPathD(dimensions.width, dimensions.height, arcHeight)

  return (
    <div
      ref={containerRef}
      className={clsx(styles.ArcText, className)}
      {...props}
    >
      <svg
        className={styles.ArcText_svg}
        viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <path ref={pathRef} id={pathId} d={pathD} fill="none" />
        </defs>
        <text
          ref={measureRef}
          className={styles.ArcText_text}
          fontSize={resolvedMaxFontSize}
          opacity={0}
          aria-hidden="true"
          pointerEvents="none"
        >
          {text}
        </text>
        <text
          className={styles.ArcText_text}
          fontSize={fontSize}
          textAnchor="middle"
          dy={Math.round(fontSize * 0.35)}
          opacity={isReady ? 1 : 0}
        >
          <textPath href={`#${pathId}`} startOffset="50%">
            {text}
          </textPath>
        </text>
      </svg>
    </div>
  )
}

export const ArcText = memo(ArcTextComponent)
